import { useFeatureFlags } from '../../hooks/useFeatureFlag';
import { usePermissions } from '../../hooks/usePermissions';
import { useEffectiveTenant } from '../../hooks/useEffectiveTenant';
import { useAppSelector } from '../../hooks/useAppStore';
import { usePhylaxBooster } from './booster/usePhylaxBooster';

/**
 * Quiénes pueden usar el CRM del Agente Virtual. Espeja `ALLOWED_ROLES` de
 * `crm.routes.ts` en el backend, que es quien realmente decide: a cualquier otro rol le
 * responde 403.
 */
export const ROLES_PHYLAX_CRM = ['agent', 'office_admin', 'tenant_admin', 'booster_admin', 'booster_support'];

/**
 * ¿Este usuario puede pedirle algo al CRM de Phylax?
 *
 * Existe porque la respuesta la necesitan CUATRO pantallas —el contador del menú, el
 * widget de la home y los dos bloques de la ficha de propiedad— y escrita cuatro veces
 * se desincronizó: el menú gateaba bien por rol, pero las consultas salían igual, con
 * `pollingInterval` de dos minutos. En una semana eso fueron 1.062 errores 403 de gente
 * que no tiene la sección: Notario Rentas (538) y los agentes de los tenants booster
 * (524), en páginas que no tienen nada que ver, porque el sidebar está en todas.
 * (11/08/2026)
 *
 * Son tres condiciones y las tres importan:
 * - **el rol**, que es lo único que mira el backend;
 * - **el tenant nativo**, porque el Agente Virtual está provisionado contra la org de
 *   Imperium y a un tenant booster la sección le abriría vacía;
 * - **el flag**, que es la perilla para apagarlo.
 *
 * Se usa como `skip` de RTK Query, así que devuelve `false` mientras los flags viajan:
 * mejor un contador que aparece medio segundo tarde que un 403 en cada carga.
 *
 * Desde el 22/10/2026 el tenant también puede ser una inmobiliaria de BOOSTER con la
 * suscripción de Phylax vigente: su org de Phylax es la suya y el backend la resuelve
 * (`ctxDelPlanDelTenant` en crm.routes). Entra por su flag, no por `phylax_crm`.
 */
export function usePuedeVerPhylaxCrm(): boolean {
  const { user } = usePermissions();
  const { flags } = useFeatureFlags();
  const { tenantMode } = useEffectiveTenant();
  const acting = useAppSelector((s) => s.auth.actingTenant);

  // Identidad EFECTIVA: en modo soporte vale el rol EMULADO del admin del tenant, para
  // que lo que se ve calce con lo que vería ese admin en su propio login.
  const rol = acting ? (acting.role ?? 'tenant_admin') : (user?.role === 'booster_admin' ? 'booster_support' : user?.role);

  // El modo tiene que ser nativo CONFIRMADO, no "no-booster": las cuentas de plataforma
  // no tienen tenant, les llega undefined, y con la comparación al revés se colaban —
  // desde el QA de Booster se veía la sección entera. Mismo criterio (y misma excepción
  // para el soporte de Imperium) que `nativeOnly` en el Sidebar. (12/08/2026)
  // OJO: contra el rol REAL, no contra `rol` — que arriba mapea booster_admin →
  // booster_support para emular la vista. Con el mapeado, el operador de plataforma
  // entraba por la excepción y volvíamos al problema original.
  const esSoporteDeImperium = !tenantMode && !acting && user?.role === 'booster_support';
  const esImperium = tenantMode === 'native' || esSoporteDeImperium;
  const { tiene: boosterConPlan } = usePhylaxBooster();

  return !!rol && ROLES_PHYLAX_CRM.includes(rol) && ((!!flags.phylax_crm && esImperium) || boosterConPlan);
}
